import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin } from "lucide-react";
import { SITE, VERIFY_NOTE } from "@/lib/site";
import { CITIES } from "@/data/cities";

const cols = [
    {
        title: "Discover",
        links: [
            { to: "/residential", label: "Residential", id: "footer-residential" },
            { to: "/commercial", label: "Commercial", id: "footer-commercial" },
            { to: "/discover", label: "All Projects", id: "footer-discover" },
        ],
    },
    {
        title: "Sthaan",
        links: [
            { to: "/about", label: "About Sthaan", id: "footer-about" },
            { to: "/developers", label: "For Developers", id: "footer-developers" },
            { to: "/contact", label: "Contact", id: "footer-contact" },
        ],
    },
    {
        title: "Legal",
        links: [
            { to: "/terms", label: "Terms & Conditions", id: "footer-terms" },
            { to: "/privacy", label: "Privacy Policy", id: "footer-privacy" },
            { to: "/disclaimer", label: "Disclaimer", id: "footer-disclaimer" },
        ],
    },
];

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="border-t border-black/5 bg-surface/40">
            <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <img
                            src="/logo.png"
                            alt="sthaan"
                            className="h-8 w-auto"
                        />
                        <p className="mt-5 max-w-sm text-sm leading-relaxed text-mist">
                            India's curated discovery platform for new residential and
                            commercial projects — presented beautifully, and connected
                            directly to the developers behind them. Zero brokerage, always.
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            <a
                                href={SITE.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-instagram-link"
                                aria-label="Sthaan on Instagram"
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-mist transition-colors hover:border-gold/50 hover:text-gold"
                            >
                                <Instagram className="h-4 w-4" />
                            </a>
                            <a
                                href={`mailto:${SITE.email}`}
                                data-testid="footer-email-link"
                                aria-label="Email Sthaan"
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-mist transition-colors hover:border-gold/50 hover:text-gold"
                            >
                                <Mail className="h-4 w-4" />
                            </a>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {CITIES.map((c) => (
                                <Link
                                    key={c.slug}
                                    to={`/city/${c.slug}`}
                                    data-testid={`footer-city-${c.slug}`}
                                    className="rounded-full border border-black/8 px-3 py-1 text-[11px] text-dim transition-colors hover:border-gold/40 hover:text-gold"
                                >
                                    {c.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7">
                        {cols.map((col) => (
                            <div key={col.title}>
                                <p className="eyebrow mb-5">{col.title}</p>
                                <ul className="space-y-3">
                                    {col.links.map((l) => (
                                        <li key={l.id}>
                                            <Link
                                                to={l.to}
                                                data-testid={l.id}
                                                className="link-line text-sm text-mist transition-colors hover:text-ivory"
                                            >
                                                {l.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-14 rounded-2xl border border-black/5 bg-obsidian/60 p-5">
                    <p className="text-xs leading-relaxed text-dim" data-testid="footer-verify-note">
                        {VERIFY_NOTE}
                    </p>
                </div>

                <div className="mt-10 border-t border-black/5 pt-8">
                    <p className="text-[11px] leading-relaxed text-dim" data-testid="footer-corporate-info">
                        Sthaan is a platform by Indexa Distribution Services Pvt. Ltd.
                        <br />
                        CIN: {SITE.cin}
                        <br />
                        <span className="inline-flex items-start gap-1.5">
                            <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                            {SITE.address}
                        </span>
                    </p>
                    <div className="mt-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                        <p className="text-[11px] text-dim">
                            © {new Date().getFullYear()} Sthaan · sthaan.space — All rights reserved.
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
                            New projects · Direct from developers · Zero brokerage
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
